/**
 * Vendor → Activity tab
 * Shows audit log entries for the vendor's party record and related AP invoices.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { auditLogQueryOptions } from "@/lib/queries";
import type { AuditLogRow } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/activity")({
  component: VendorActivityTab,
});

const ACTION_COLOR: Record<string, string> = {
  INSERT: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  UPDATE: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

const TABLE_LABEL: Record<string, string> = {
  parties: "Vendor",
  ap_invoices: "AP Invoice",
  payments: "Payment",
  purchase_orders: "PO",
  lien_waivers: "Lien Waiver",
  subcontracts: "Subcontract",
  contacts: "Contact",
  vendor_qualifications: "Qualification",
};

function VendorActivityTab() {
  const { vendorId } = Route.useParams();

  // Audit log for the vendor's party row
  const partyLogQ = useQuery(
    auditLogQueryOptions({ table_name: "parties", row_id: vendorId, limit: 50 }),
  );

  // Audit log for AP invoices linked to this vendor
  const apLogQ = useQuery(
    auditLogQueryOptions({ table_name: "ap_invoices", limit: 100 }),
  );

  const partyRows: AuditLogRow[] = partyLogQ.data?.rows ?? [];
  // We can't filter ap_invoices by vendor_id directly via audit log, so just show party changes for now
  const allRows = [...partyRows].sort(
    (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
  );

  const isLoading = partyLogQ.isLoading;

  return (
    <div className="p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          Activity
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Audit trail for this vendor record
        </p>
      </div>

      {isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}

      {!isLoading && allRows.length === 0 && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No activity recorded yet.
        </div>
      )}

      {allRows.length > 0 && (
        <div className="space-y-2">
          {allRows.map((row) => {
            const changedFields = row.changed_fields ?? [];
            const oldData = row.old_data ?? {};
            const newData = row.new_data ?? {};

            return (
              <div
                key={row.id}
                className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-4 py-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`px-1.5 py-0.5 text-xs rounded font-medium ${ACTION_COLOR[row.action] ?? ""}`}
                  >
                    {row.action}
                  </span>
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {TABLE_LABEL[row.table_name] ?? row.table_name}
                  </span>
                  <span className="flex-1" />
                  <span className="text-xs text-slate-400 dark:text-slate-500">
                    {new Date(row.at).toLocaleString()}
                  </span>
                </div>
                {changedFields.length > 0 && (
                  <div className="text-xs text-slate-600 dark:text-slate-400 space-y-0.5">
                    {changedFields.map((f) => (
                      <div key={f}>
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {f}
                        </span>
                        :{" "}
                        <span className="line-through text-red-500 dark:text-red-400">
                          {String(oldData[f] ?? "—")}
                        </span>{" "}
                        →{" "}
                        <span className="text-green-600 dark:text-green-400">
                          {String(newData[f] ?? "—")}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {row.action === "INSERT" && (
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Record created
                    {newData.name ? ` — ${String(newData.name)}` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
